import _ from 'lodash';
import {addMonths} from 'date-fns';
import {amountWithComma, amountWithPrecisionAsRequiredAndComma} from 'javascripts/utils/numberFormatter';
import {computed, observable} from 'mobx';
import {computedFn} from 'mobx-utils';

export default class {
  donationSliderConstant = 50;

  @observable showPrivacyOptions = false;
  @observable showLifetimeImpact = false;
  @observable country = 'us';
  @observable graphData = [];
  @observable lifetimeImpactTotals = {};
  @observable showCountryOptions = false;
  @observable exploreButtonClicked = false;
  @observable numberOfReferrals = 0;
  @observable numberOfProjectedMonths = 6;
  @observable usCostPerPerson = 0;
  @observable ukCostPerPerson = 0;
  @observable maxAdditionalReferrals = 20;
  @observable explorePanelHeight = 400;
  @observable openSocialShareModal = false;
  @observable projectedMonthlyDonationAmount = 0;

  @computed get graphHeight() {
    return this.explorePanelHeight - 10;
  }

  @computed get shouldShowCountrySwitcher() {
    return this.lifetimeImpactTotals['us'].total > 0 && this.lifetimeImpactTotals['uk'].total > 0;
  }

  @computed get isUs() {
    return this.country == 'us';
  }

  @computed get isUk() {
    return this.country == 'uk';
  }

  @computed get currencyUnit() {
    return {
      us: '$',
      uk: '£',
    }[this.country];
  }

  @computed get projectedReferralSubAmount() {
    return {
      us: 20,
      uk: 15,
    }[this.country];
  }

  @computed get selectedCostPerPerson() {
    return this.country == 'us' ? this.usCostPerPerson : this.ukCostPerPerson;
  }

  formatProjectedAmount = computedFn(amount => `${this.currencyUnit}${amountWithComma(amount)}`);

  formatActualAmount = computedFn(amount => `${this.currencyUnit}${amountWithPrecisionAsRequiredAndComma(amount)}`);

  @computed get formattedPeopleServed() {
    return `${amountWithComma(this.selectedLifetimeImpact.peopleServed)}`;
  }

  @computed get selectedLifetimeImpact() {
    return this.lifetimeImpactTotals[this.country];
  }

  @computed get currentActualDonationTotals() {
    return _.last(this.graphData);
  }

  @computed get projectedDonationTotals() {
    return _.last(this.graphDataWithProjectedMonths);
  }

  @computed get totalProjectedPeopleServed() {
    return this.additionalProjectedPeopleServed + this.selectedLifetimeImpact.peopleServed;
  }

  @computed get additionalProjectedPeopleServed() {
    return Math.floor((this.projectedDonationTotals.total - this.selectedLifetimeImpact.total) / this.selectedCostPerPerson);
  }

  @computed get projectedMonthlyImpact() {
    return this.projectedMonthlyReferralAmount + this.projectedMonthlyDonationAmount + this.selectedLifetimeImpact.subscriptionReferralMonthlyAmount;
  }

  @computed get projectedMonthlyReferralAmount() {
    const numProjectedReferrals = Math.max(this.numberOfReferrals - this.selectedLifetimeImpact.subscriptionReferralCount, 0);
    return numProjectedReferrals * this.projectedReferralSubAmount;
  }

  @computed get maxReferralCount() {
    return this.selectedLifetimeImpact.subscriptionReferralCount + this.maxAdditionalReferrals;
  }

  @computed get maxProjectedMonthlyReferralAmount() {
    return this.maxReferralCount * this.projectedReferralSubAmount;
  }

  @computed({ keepAlive: true }) get minimumMonthlyDonationForSlider() {
    return Math.max(this.selectedLifetimeImpact.subscriptionAmount, 5);
  }

  @computed get maxProjectedMonthlyImpact() {
    return this.maxProjectedMonthlyReferralAmount + this.selectedLifetimeImpact.subscriptionReferralMonthlyAmount + this.maximumMonthlyDonationForSlider;
  }

  @computed get maximumMonthlyDonationForSlider() {
    return this.minimumMonthlyDonationForSlider + this.donationSliderConstant;
  }

  @computed get graphDataWithProjectedMonths() {
    const projectedData = [];
    const { fundraising } = this.currentActualDonationTotals;
    let maxProjectedTotal;

    let {
      date,
      total,
      donations,
      subscriptionReferralDonations,
    } = this.currentActualDonationTotals;

    maxProjectedTotal = total;

    _.range(0, this.numberOfProjectedMonths).forEach(() => {
      date = addMonths(new Date(date), 1);
      total += this.projectedMonthlyImpact;
      donations += this.projectedMonthlyDonationAmount;
      subscriptionReferralDonations += this.selectedLifetimeImpact.subscriptionReferralMonthlyAmount + this.projectedMonthlyReferralAmount;
      maxProjectedTotal += this.maxProjectedMonthlyImpact;

      projectedData.push({
        date: date.toISOString(),
        total: total,
        donations: donations,
        fundraising: fundraising,
        subscriptionReferralDonations: subscriptionReferralDonations,
        isProjected: true,
        maxProjectedTotal: maxProjectedTotal,
      });
    });

    return this.graphData.slice().concat(projectedData);
  }
}
